import { Box, Button, Stack, useTheme } from "@mui/material";
import { CollapseButton } from "./Components";
import { EditOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { SIDEBAR_WIDTH, UI_ELEMENTS } from "Common/Constants";
import { useMatchDownMd } from "Common/Hooks";
import { IConversationList, IConversationTitle } from "Common/Interfaces/Domain/Conversations";
import { useSidebar } from "./Hooks/useSidebar";
import { CircularLoading, ConfirmationDialog, ContextMenu } from "Common/Components";
import GroupedConversations from "./Components/GroupedConversations";
import { getConfirmationContent, getContextMenuItems } from "./Functions";
import { CHAT_GROUPS } from "./Constants";
import { IUIElementState } from "Common/Interfaces";

export interface ISideBar {
  isCollapsed?: boolean;
  setIsCollapsed?: React.Dispatch<React.SetStateAction<boolean>>;
  uiElementState?: IUIElementState<IConversationTitle>;
  handleCloseUIElement?: () => void;
  setUIElementState: React.Dispatch<React.SetStateAction<IUIElementState<IConversationTitle>>>;
}

export const SideBar = (props: ISideBar) => {
  const { isCollapsed, setIsCollapsed, uiElementState, setUIElementState, handleCloseUIElement } = props;
  const { matchDownMd } = useMatchDownMd();
  const theme = useTheme();
  const [data, setData] = useState<IConversationList>();

  const {
    groupConversationsByDateRange,
    conversationList,
    isConversationsLoading,
    isDeleteLoading,
    handleConfirmationDialogEvent,
    handleContextMenuButtonClick,
    handleContextMenuItemClick,
    handleTitleRename,
    editChatTitleId,
    setEditChatTitleId,
    navigateToStart,
    chatId,
    conversationLoading,
    handleNavigateToChat,
    handleOpenSnackbar,
  } = useSidebar({
    handleCloseUIElement,
    setUIElementState,
    uiElementState,
  });

  useEffect(() => {
    setData(conversationList);
  }, [conversationList, isConversationsLoading]);

  let groupedConversations: Record<string, IConversationTitle[]> | null = null;

  if (!isConversationsLoading && data && data?.conversations.length > 0) {
    groupedConversations = groupConversationsByDateRange(data?.conversations);
  }

  const [isGroupOpen, setIsGroupOpen] = useState({
    [CHAT_GROUPS.TODAY]: true,
    [CHAT_GROUPS.YESTERDAY]: true,
    [CHAT_GROUPS.PREV_7_DAYS]: true,
    [CHAT_GROUPS.PREV_30_DAYS]: true,
    [CHAT_GROUPS.EARLIER]: true,
  });

  const handleClick = (groupTitle: keyof typeof isGroupOpen) => {
    setIsGroupOpen((prevState) => ({
      ...prevState,
      [groupTitle]: !prevState[groupTitle],
    }));
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: { xs: 1, md: 0 },
        flexGrow: { xs: 1, md: 0 },
        marginLeft: isCollapsed ? `-${SIDEBAR_WIDTH}px` : "0px",
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.leavingScreen,
        }),
        backgroundColor: theme.palette.background.paper,
        position: { md: "relative" },
      }}
    >
      {!matchDownMd && (
        <CollapseButton
          isCollapsed={isCollapsed ?? false}
          onClick={() => setIsCollapsed?.(!isCollapsed)}
          sx={{
            right: isCollapsed ? 0 : 5,
            top: "calc(50% - 48px)",
            transform: "translate(100%, -50%)",
          }}
        />
      )}
      <Stack
        direction={"column"}
        sx={{ paddingTop: 2, visibility: isCollapsed ? "hidden" : "visible", height: "100%" }}
        spacing={2}
      >
        <Button
          variant="contained"
          startIcon={<EditOutlined fontSize="small" />}
          onClick={navigateToStart}
          disabled={conversationLoading}
        >
          Start New Chat
        </Button>

        <Box sx={{ display: "flex", flexDirection: "column", overflowY: "auto", flex: 1 }}>
          <CircularLoading isLoading={isConversationsLoading}>
            <React.Fragment>
              {groupedConversations &&
                Object.entries(groupedConversations).map(([groupTitle, conversations]) => {
                  return conversations.length ? (
                    <GroupedConversations
                      key={groupTitle}
                      groupTitle={groupTitle}
                      conversations={conversations}
                      chatId={chatId}
                      editChatTitleId={editChatTitleId}
                      open={isGroupOpen[groupTitle as keyof typeof isGroupOpen]}
                      handleClick={() => handleClick(groupTitle as keyof typeof isGroupOpen)}
                      handleContextMenuButtonClick={handleContextMenuButtonClick}
                      handleTitleRename={handleTitleRename}
                      setEditChatTitleId={setEditChatTitleId}
                      isContextMenuOpen={uiElementState?.activeElement === UI_ELEMENTS.CONTEXT_MENU}
                      contextMenuOpenId={uiElementState?.dataRow?.conversationId}
                      handleNavigate={handleNavigateToChat}
                      isConversationLoading={conversationLoading}
                      handleSnackbar={handleOpenSnackbar}
                    />
                  ) : null;
                })}
            </React.Fragment>
          </CircularLoading>
        </Box>
      </Stack>
      <ConfirmationDialog
        confirmationContent={getConfirmationContent({
          activeElement: uiElementState?.activeElement ?? null,
          dataRow: uiElementState?.dataRow ?? null,
          isLoadingDeleteChat: isDeleteLoading,
        })}
        open={uiElementState?.activeElement === UI_ELEMENTS.DELETE_CONFIRMATION}
        dialogEvent={handleConfirmationDialogEvent}
      />

      <ContextMenu
        open={Boolean(uiElementState?.activeElement === UI_ELEMENTS.CONTEXT_MENU)}
        anchorEl={uiElementState?.activeElement === UI_ELEMENTS.CONTEXT_MENU ? uiElementState.anchorEl : null}
        handleClose={() => {
          handleCloseUIElement?.();
        }}
        menuItems={getContextMenuItems(handleContextMenuItemClick)}
      />
    </Box>
  );
};
